class AmbientaOrderStatus extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-order-status-form]');
    if (!this.form || this.form.dataset.ready === 'true') return;
    this.form.dataset.ready = 'true';
    this.endpoint = this.dataset.endpoint || '/apps/order-status';
    this.message = this.querySelector('[data-order-status-message]');
    this.result = this.querySelector('[data-order-status-result]');
    this.submit = this.form.querySelector('button[type="submit"]');
    this.spinner = this.submit.querySelector('[data-spinner]');
    this.form.addEventListener('submit', event => this.handleSubmit(event));
    this.form.querySelectorAll('input[name="identityType"]').forEach(radio => radio.addEventListener('change', () => this.updateIdentityField()));
    this.updateIdentityField();
  }

  updateIdentityField() {
    const type = this.form.elements.identityType.value;
    const field = this.form.elements.identity;
    field.type = type === 'phone' ? 'tel' : 'email';
    field.inputMode = type === 'phone' ? 'tel' : 'email';
    field.autocomplete = type === 'phone' ? 'tel' : 'email';
    field.placeholder = type === 'phone' ? '6036-3433' : 'tu@correo.com';
  }

  setLoading(loading) {
    this.submit.disabled = loading;
    this.submit.setAttribute('aria-busy', String(loading));
    this.spinner.hidden = !loading;
    this.submit.querySelector('[data-label]').textContent = loading ? 'Consultando…' : 'Consultar pedido';
  }

  showMessage(text = '', type = 'error') {
    this.message.textContent = text;
    this.message.className = `aos-message aos-message--${type}`;
    this.message.hidden = !text;
  }

  clearErrors() {
    this.form.querySelectorAll('[data-field-error]').forEach(node => { node.textContent = ''; });
    this.form.querySelectorAll('[aria-invalid=true]').forEach(node => node.setAttribute('aria-invalid', 'false'));
  }

  fieldError(field, text) {
    field.setAttribute('aria-invalid', 'true');
    const error = this.form.querySelector(`[data-field-error="${field.name}"]`);
    if (error) error.textContent = text;
  }

  validate(orderNumber, identityType, identity) {
    let valid = true;
    const orderField = this.form.elements.orderNumber;
    const identityField = this.form.elements.identity;
    if (!/^#?\s*\d{1,12}$/.test(orderNumber.trim())) {
      this.fieldError(orderField, 'Ingresa un número de pedido válido, por ejemplo #13450.'); valid = false;
    }
    if (identityType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identity.trim())) {
      this.fieldError(identityField, 'Ingresa el correo utilizado en la compra.'); valid = false;
    }
    if (identityType === 'phone') {
      const digits = identity.replace(/\D/g, '').replace(/^503(?=\d{8}$)/, '');
      if (!/^\d{8}$/.test(digits)) { this.fieldError(identityField, 'Ingresa un teléfono salvadoreño de 8 dígitos.'); valid = false; }
    }
    return valid;
  }

  formatDate(value) {
    if (!value) return '';
    try {
      return new Intl.DateTimeFormat('es-SV', {timeZone: 'America/El_Salvador', day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true}).format(new Date(value)).replace(/a\.\s*m\./i, 'a. m.').replace(/p\.\s*m\./i, 'p. m.');
    } catch { return ''; }
  }

  escape(value) {
    const node = document.createElement('span'); node.textContent = String(value ?? ''); return node.innerHTML;
  }

  render(order) {
    const timeline = order.timeline.map((step, index) => {
      const symbol = step.state === 'complete' ? '✓' : step.state === 'current' ? '●' : step.state === 'attention' ? '!' : String(index + 1);
      const date = step.at ? `<p class="aos-step__date">${this.escape(this.formatDate(step.at))}</p>` : '';
      const note = step.note ? `<p>${this.escape(step.note)}</p>` : '';
      return `<li class="aos-step aos-step--${this.escape(step.state)}"><span class="aos-step__icon" aria-hidden="true">${symbol}</span><div class="aos-step__content"><strong>${this.escape(step.label)}${step.state === 'current' ? ' — Estado actual' : ''}</strong><p>${this.escape(step.description)}</p>${date}${note}</div></li>`;
    }).join('');
    const guide = order.tracking ? `<section class="aos-guide" aria-labelledby="aos-guide-title"><div><h3 id="aos-guide-title">Número de guía</h3><p>Para consultar el movimiento registrado por Express El Salvador, copia esta guía y colócala en su página de seguimiento.</p><strong class="aos-guide__number" data-guide>${this.escape(order.tracking.number)}</strong></div><div class="aos-guide__actions"><button class="aos-guide__copy" type="button" data-copy-guide>Copiar guía</button><a class="aos-guide__link" href="https://expresselsalvador.sistrack.net/track" target="_blank" rel="noopener noreferrer">Abrir seguimiento externo</a></div></section>` : '';
    this.result.innerHTML = `<article class="aos-card"><header class="aos-card__top"><div><p class="aos-card__kicker">Consulta segura</p><h2 class="aos-card__order">Pedido ${this.escape(order.orderNumber)}</h2></div><span class="aos-badge">${this.escape(order.statusLabel)}</span></header><div class="aos-card__current"><h3>${this.escape(order.statusLabel)}</h3><p>${this.escape(order.statusDescription)}</p>${order.publicNote ? `<p class="aos-public-note">${this.escape(order.publicNote)}</p>` : ''}<p class="aos-updated">Última actualización: ${this.escape(this.formatDate(order.updatedAt))} · Hora de El Salvador</p></div><ol class="aos-timeline" aria-label="Progreso del pedido">${timeline}</ol></article>${guide}`;
    this.result.hidden = false;
    this.result.querySelector('[data-copy-guide]')?.addEventListener('click', async event => {
      try { await navigator.clipboard.writeText(order.tracking.number); event.currentTarget.textContent = 'Guía copiada'; } catch { event.currentTarget.textContent = order.tracking.number; }
    });
    this.result.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'});
  }

  async handleSubmit(event) {
    event.preventDefault(); this.clearErrors(); this.showMessage(); this.result.hidden = true; this.result.innerHTML = '';
    const data = new FormData(this.form); const orderNumber = String(data.get('orderNumber') || ''); const identityType = String(data.get('identityType') || 'email'); const identity = String(data.get('identity') || '');
    if (!this.validate(orderNumber, identityType, identity)) return;
    this.setLoading(true); const controller = new AbortController(); const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(this.endpoint, {method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}, body: JSON.stringify({orderNumber, identityType, identity}), signal: controller.signal, credentials: 'same-origin'});
      const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.message || 'No pudimos consultar tu pedido en este momento.');
      this.render(payload.order);
    } catch (error) { this.showMessage(error.name === 'AbortError' ? 'La consulta tardó más de lo esperado. Intenta nuevamente.' : error.message); }
    finally { clearTimeout(timeout); this.setLoading(false); }
  }
}
if (!customElements.get('ambienta-order-status')) customElements.define('ambienta-order-status', AmbientaOrderStatus);
